import type { ApiErrorBody, AuthResponse } from '@copilot/shared';
import { sessionStore } from '../auth/session';
import { toQueryString } from '../lib/meetingFilters';
import { filenameFromDisposition } from '../lib/text';

/**
 * fetch wrapper for the /v1 API:
 * - attaches the in-memory Bearer token,
 * - on 401 performs one refresh (httpOnly cookie) and retries once,
 * - otherwise clears the session, which makes <RequireAuth> redirect to /login,
 * - turns error responses into `ApiError`.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message || `Request failed (${err.status})`;
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}

async function toApiError(res: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> | null = null;
  try {
    body = (await res.json()) as Partial<ApiErrorBody>;
  } catch {
    /* not JSON */
  }
  const fallback = res.status === 403 ? 'You do not have permission to do that.' : `Request failed (${res.status})`;
  return new ApiError(res.status, body?.error ?? 'error', body?.message ?? fallback);
}

let refreshing: Promise<boolean> | null = null;

/**
 * Exchange the refresh cookie for a new access token. Concurrent callers share
 * one request (the server rotates the refresh token on every use).
 */
export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const res = await fetch('/v1/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: '{}',
      });
      if (!res.ok) {
        sessionStore.set(null);
        return false;
      }
      const data = (await res.json()) as AuthResponse;
      sessionStore.set({ accessToken: data.accessToken, user: data.user });
      return true;
    } catch {
      sessionStore.set(null);
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Query;
  /** JSON-serialisable body, or FormData for multipart uploads. */
  body?: unknown;
}

function buildInit(opts: RequestOptions): RequestInit {
  const headers = new Headers();
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) {
    body = opts.body;
  } else if (opts.body !== undefined) {
    // Only send a JSON content-type with a body: Fastify rejects empty JSON bodies.
    headers.set('content-type', 'application/json');
    body = JSON.stringify(opts.body);
  }
  return { method: opts.method ?? 'GET', headers, body, credentials: 'include' };
}

/** Low-level request returning the raw Response (throws ApiError on non-2xx). */
export async function apiFetch(path: string, opts: RequestOptions = {}): Promise<Response> {
  const url = `/v1${path}${opts.query ? toQueryString(opts.query) : ''}`;
  const send = () => {
    const init = buildInit(opts);
    const token = sessionStore.get()?.accessToken;
    if (token) (init.headers as Headers).set('authorization', `Bearer ${token}`);
    return fetch(url, init);
  };

  let res = await send();
  if (res.status === 401 && !path.startsWith('/auth/')) {
    if (await refreshSession()) res = await send();
    if (res.status === 401) sessionStore.set(null);
  }
  if (!res.ok) throw await toApiError(res);
  return res;
}

/** JSON request. Returns undefined for empty responses (204). */
export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await apiFetch(path, opts);
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Download a file from an authenticated endpoint (bearer auth rules out a plain <a href>). */
export async function downloadFile(path: string, fallbackName: string, query?: Query): Promise<void> {
  const res = await apiFetch(path, { query });
  const blob = await res.blob();
  const name = filenameFromDisposition(res.headers.get('content-disposition'), fallbackName);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
